import { createSelector } from '@reduxjs/toolkit';

import { LoginDataSliceState } from '../store/logInDataSlice';

const dockerDomain = process.env.DOCKER_DOMAIN;
const dockerPrLabel = process.env.DOCKER_PR_LABEL;

interface ClientUrls {
  readonly accountsUrl: string;
  readonly convertUrl: string;
  readonly designUrl: string;
  readonly markupUrl: string;
  readonly materialsUrl: string;
  readonly packingUrl: string;
  readonly pricingUrl: string;
  readonly productionUrl: string;
  readonly projectsUrl: string;
  readonly reviewUrl: string;
  readonly scheduleUrl: string;
  readonly servicesUrl: string;
}

interface EnvironmentClientUrls {
  readonly localhost: ClientUrls;
  readonly develop: ClientUrls;
  readonly 'release-candidate': ClientUrls;
  readonly production: ClientUrls;
  readonly docker: ClientUrls;
}

const defaultClientUrls: EnvironmentClientUrls = {
  localhost: {
    accountsUrl: process.env.PARAGON_LOCAL_ACCOUNTS_URL ?? 'http://localhost:9006/',
    convertUrl: process.env.PARAGON_LOCAL_CONVERT_URL ?? 'http://localhost:9010/',
    designUrl: process.env.PARAGON_LOCAL_DESIGN_URL ?? 'http://localhost:9005/',
    markupUrl: process.env.PARAGON_LOCAL_MARKUP_URL ?? 'http://localhost:9009/',
    materialsUrl: process.env.PARAGON_LOCAL_MATERIALS_URL ?? 'http://localhost:9014/',
    packingUrl: process.env.PARAGON_LOCAL_PACKING_URL ?? 'http://localhost:9013/',
    pricingUrl: process.env.PARAGON_LOCAL_PRICING_URL ?? 'http://localhost:9011/',
    productionUrl: process.env.PARAGON_LOCAL_PRODUCTION_URL ?? 'http://localhost:9016/',
    projectsUrl: process.env.PARAGON_LOCAL_PROJECTS_URL ?? 'http://localhost:9012/',
    reviewUrl: process.env.PARAGON_LOCAL_REVIEW_URL ?? 'http://localhost:9007/',
    scheduleUrl: process.env.PARAGON_LOCAL_SCHEDULE_URL ?? 'http://localhost:9017/',
    servicesUrl: process.env.PARAGON_LOCAL_SERVICES_URL ?? 'http://localhost:9117/',
  },
  develop: {
    accountsUrl: 'https://developaccounts.paragontruss.com/',
    convertUrl: 'https://developconvert.paragontruss.com/',
    designUrl: 'https://developdesign.paragontruss.com/',
    markupUrl: 'https://developmarkup.paragontruss.com/',
    materialsUrl: 'https://developmaterials.paragontruss.com/',
    packingUrl: 'https://developpacking.paragontruss.com/',
    pricingUrl: 'https://developpricing.paragontruss.com/',
    productionUrl: 'https://developproduction.paragontruss.com/',
    projectsUrl: 'https://developprojects.paragontruss.com/',
    reviewUrl: 'https://developreview.paragontruss.com/',
    scheduleUrl: 'https://developschedule.paragontruss.com/',
    servicesUrl: 'https://developservices.paragontruss.com/',
  },
  'release-candidate': {
    accountsUrl: 'https://rcaccounts.paragontruss.com/',
    convertUrl: 'https://rccconvert.paragontruss.com/',
    designUrl: 'https://rcdesign.paragontruss.com/',
    markupUrl: 'https://rcmarkup.paragontruss.com/',
    materialsUrl: 'https://rcmaterials.paragontruss.com/',
    packingUrl: 'https://rcpacking.paragontruss.com/',
    pricingUrl: 'https://rcpricing.paragontruss.com/',
    productionUrl: 'https://rcproduction.paragontruss.com/',
    projectsUrl: 'https://rcprojects.paragontruss.com/',
    reviewUrl: 'https://rcreview.paragontruss.com/',
    scheduleUrl: 'https://rcschedule.paragontruss.com/',
    servicesUrl: 'https://rcservices.paragontruss.com/',
  },
  production: {
    accountsUrl: 'https://accounts.paragontruss.com/',
    convertUrl: 'https://convert.paragontruss.com/',
    designUrl: 'https://design.paragontruss.com/',
    markupUrl: 'https://markup.paragontruss.com/',
    materialsUrl: 'https://materials.paragontruss.com/',
    packingUrl: 'https://packing.paragontruss.com/',
    pricingUrl: 'https://pricing.paragontruss.com/',
    productionUrl: 'https://production.paragontruss.com/',
    projectsUrl: 'https://projects.paragontruss.com/',
    reviewUrl: 'https://review.paragontruss.com/',
    scheduleUrl: 'https://schedule.paragontruss.com/',
    servicesUrl: 'https://services.paragontruss.com/',
  },
  docker: {
    accountsUrl: `https://${dockerPrLabel}accounts.${dockerDomain}/`,
    convertUrl: `https://${dockerPrLabel}convert.${dockerDomain}/`,
    designUrl: `https://${dockerPrLabel}design.${dockerDomain}/`,
    markupUrl: `https://${dockerPrLabel}markup.${dockerDomain}/`,
    materialsUrl: `https://${dockerPrLabel}materials.${dockerDomain}/`,
    packingUrl: `https://${dockerPrLabel}packing.${dockerDomain}/`,
    pricingUrl: `https://${dockerPrLabel}pricing.${dockerDomain}/`,
    productionUrl: `https://${dockerPrLabel}production.${dockerDomain}/`,
    projectsUrl: `https://${dockerPrLabel}projects.${dockerDomain}/`,
    reviewUrl: `https://${dockerPrLabel}review.${dockerDomain}/`,
    scheduleUrl: `https://${dockerPrLabel}schedule.${dockerDomain}/`,
    servicesUrl: `https://${dockerPrLabel}services.${dockerDomain}/`,
  },
};

export const getClientUrls = createSelector(
  (state: LoginDataSliceState) => state.environment.activeEnvironment,
  (activeEnvironment): ClientUrls => {
    // Add conditionals for white-labeled organizations here

    if (!Object.keys(defaultClientUrls).includes(activeEnvironment)) {
      throw new Error(`No client URLs defined for environment: ${activeEnvironment}`);
    }

    return defaultClientUrls[activeEnvironment as keyof EnvironmentClientUrls];
  },
);

const clientDisplayNames: Readonly<Record<keyof ClientUrls, string>> = {
  accountsUrl: 'Accounts',
  convertUrl: 'Convert',
  designUrl: 'Design',
  markupUrl: 'Markup',
  materialsUrl: 'Materials',
  packingUrl: 'Packing',
  pricingUrl: 'Pricing',
  productionUrl: 'Production',
  projectsUrl: 'Projects',
  reviewUrl: 'Review',
  scheduleUrl: 'Schedule',
  servicesUrl: 'Services',
};

const withoutTrailingSlash = (url: string) => url.replace(/\/+$/, '');

/**
 * Which Paragon client the user is currently in, derived by matching the page's origin against the (already
 * environment-aware) client URL map.
 *
 * This is deliberately derived rather than passed in by each client: it avoids threading an app-name prop through
 * every client's root component, and it resolves correctly on localhost, develop, RC, production, and the
 * per-PR Docker deployments alike.
 *
 * Returns `null` for anything not in the map — the boilerplate template, Storybook, or a host we don't recognize.
 */
export const getCurrentClientName = createSelector(getClientUrls, (urls): string | null => {
  const currentOrigin = withoutTrailingSlash(window.location.origin);

  const matchingKey = (Object.keys(clientDisplayNames) as (keyof ClientUrls)[]).find(
    (key) => withoutTrailingSlash(urls[key]) === currentOrigin,
  );

  return matchingKey ? clientDisplayNames[matchingKey] : null;
});
